"use client";

import React, { useLayoutEffect, useState } from "react";
import { useScroll, useTransform, motion } from "framer-motion";
import { useRef } from "react";
import { hardcodedContent } from "@/app/(site)/_lib/global-content";
import Marquee from "react-fast-marquee";
import Image from "next/image";
import footerArrow from "@assets/images/footer-arrow.svg";
import ButtonCta from "../button-cta/ButtonCta";
import Loader from "../loader/Loader";
import FooterColsText from "./FooterColsText";

export default function FooterWhiteBlock() {
	const containerRef = useRef<HTMLDivElement>(null);
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

	useLayoutEffect(() => {
		const updateMainPadding = () => {
			const footerLogotype = document.getElementById("footer-logotype");
			const mainElement = document.querySelector("main");

			if (footerLogotype && mainElement) {
				requestAnimationFrame(() => {
					const footerLogotypeHeight = footerLogotype.getBoundingClientRect().height;
					mainElement.style.marginBottom = `${footerLogotypeHeight}px`;
				});
			}
		};

		updateMainPadding();

		const observer = new MutationObserver(updateMainPadding);
		const footerLogotype = document.getElementById("footer-logotype");
		if (footerLogotype) {
			observer.observe(footerLogotype, {
				childList: true,
				subtree: true,
				attributes: true,
				characterData: true,
			});
		}

		window.addEventListener("resize", updateMainPadding);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", updateMainPadding);
		};
	}, []);

	const { scrollYProgress } = useScroll({
		target: containerRef,
		offset: ["end end", "end center"],
	});

	// const marginX = useTransform(scrollYProgress, [0, 0.5, 1], [0, 22.5, 22.5]);
	const borderRadius = useTransform(scrollYProgress, [0, 0.5, 1], [0, 10, 10]);
	// const marginTop = useTransform(scrollYProgress, [0, 0.5, 1], [40, 0, 0]);
	// const paddingTop = useTransform(scrollYProgress, [0, 0.5, 1], [0, 20, 20]);

	const marqueeItems = [
		{ type: "text", content: hardcodedContent.footerWhiteBlock.letsConnect },
		{ type: "arrow" },
		{ type: "text", content: hardcodedContent.footerWhiteBlock.letsGrow },
		{ type: "arrow" },
		{ type: "text", content: hardcodedContent.footerWhiteBlock.letsMakeItHappenTogether },
		{ type: "arrow" },
	];
	const doubleMarqueeItems = [...marqueeItems, ...marqueeItems];

	const handleSubscribe = async () => {
		if (!email.trim() || !email.includes("@")) return;

		setIsSubmitting(true);
		setSubmitStatus("idle");

		try {
			const response = await fetch("/api/subscribe", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ email: email.trim() }),
			});

			if (response.ok) {
				setEmail("");
				setSubmitStatus("success");
				setTimeout(() => setSubmitStatus("idle"), 4000);
			} else {
				setSubmitStatus("error");
				setTimeout(() => setSubmitStatus("idle"), 4000);
			}
		} catch {
			setSubmitStatus("error");
			setTimeout(() => setSubmitStatus("idle"), 4000);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<motion.div
			ref={containerRef}
			id="footer-white-block"
			className="after:bg-navy-blue-deep bg-navy-blue-deep relative -mt-0.5 after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:content-['']"
			style={
				{
					// paddingLeft: marginX,
					// paddingRight: marginX,
					// marginTop: marginTop,
					// paddingTop: paddingTop,
				}
			}
		>
			<motion.div
				className="after:bg-navy-blue-deep relative h-full bg-white pb-5 after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:content-[''] sm:pb-10"
				style={{
					borderBottomLeftRadius: borderRadius,
					borderBottomRightRadius: borderRadius,
				}}
			>
				<div className="flex flex-col gap-y-24">
					<div className="pt-5 sm:pt-10">
						<Marquee autoFill={false} speed={250}>
							{doubleMarqueeItems.map((item, index) => (
								<React.Fragment key={index}>
									{item.type === "text" ? (
										<span className="text-h5 sm:text-h1 text-graphite-black mx-1.5 sm:mx-[0.781rem]">
											{item.content}
										</span>
									) : (
										<div className="relative mx-1.5 size-10 sm:mx-[0.781rem] sm:size-20">
											<Image src={footerArrow} fill sizes="100%" alt="footer arrow" className="object-contain" />
										</div>
									)}
								</React.Fragment>
							))}
						</Marquee>
					</div>
					<div className="grid grid-cols-2 gap-x-5 px-3 sm:grid-cols-12 md:px-10">
						<div className="col-span-2 sm:col-span-5 sm:self-end md:col-span-4">
							<h6 className="text-h7-mob sm:text-h6 text-graphite-black">
								{hardcodedContent.footerWhiteBlock.textAboveInput1}
							</h6>
							<h6 className="text-h7-mob sm:text-h6 text-graphite-black">
								{hardcodedContent.footerWhiteBlock.textAboveInput2}
							</h6>
							<div className="relative">
								<div className="xxs:flex-row mt-5 flex flex-col items-center justify-between gap-4 rounded-lg bg-black/5 px-5 py-6">
									<input
										type="email"
										name="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										placeholder={hardcodedContent.footerWhiteBlock.enterEmail}
										className="text-body-s sm:text-body-m text-graphite-black/40 placeholder:text-graphite-black/40 placeholder:text-body-s sm:placeholder:text-body-m xxs:placeholder:text-left w-full max-w-[90%] bg-transparent outline-none placeholder:text-center"
										disabled={isSubmitting}
									/>
									{isSubmitting ? (
										<Loader />
									) : (
										<ButtonCta label={hardcodedContent.footerWhiteBlock.subscribe} onClick={handleSubscribe} />
									)}
								</div>

								{submitStatus === "success" && (
									<div className="border-success bg-success absolute top-full right-0 left-0 z-10 mt-2 rounded-lg border p-4">
										<p className="text-body-s text-center text-white">
											{hardcodedContent.footerWhiteBlock.subscribeSuccess}
										</p>
									</div>
								)}

								{submitStatus === "error" && (
									<div className="border-error bg-error absolute top-full right-0 left-0 z-10 mt-2 rounded-lg border p-4">
										<p className="text-body-s text-center text-white">
											{hardcodedContent.footerWhiteBlock.subscribeError}
										</p>
									</div>
								)}
							</div>
						</div>
						<div className="hidden sm:col-span-7 sm:col-start-6 sm:flex sm:justify-between">
							<FooterColsText />
						</div>
					</div>
				</div>
			</motion.div>
		</motion.div>
	);
}
